import React, { useEffect, useState, useRef } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { scaleQuantile } from 'd3-scale';
import { MapPin } from 'lucide-react';
import { IntelligenceLayout, fetchWithAuth } from './IntelligenceLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const COLOR_RANGE = [
  '#0A1F44', '#1e3a5f', '#2563eb', '#3b82f6',
  '#60a5fa', '#fbbf24', '#f59e0b', '#D4AF37',
];
const NO_DATA_COLOR = '#cbd5e1';

const STATE_NAME_TO_ABBR = {
  Alabama:'AL',Alaska:'AK',Arizona:'AZ',Arkansas:'AR',California:'CA',
  Colorado:'CO',Connecticut:'CT',Delaware:'DE',Florida:'FL',Georgia:'GA',
  Hawaii:'HI',Idaho:'ID',Illinois:'IL',Indiana:'IN',Iowa:'IA',Kansas:'KS',
  Kentucky:'KY',Louisiana:'LA',Maine:'ME',Maryland:'MD',Massachusetts:'MA',
  Michigan:'MI',Minnesota:'MN',Mississippi:'MS',Missouri:'MO',Montana:'MT',
  Nebraska:'NE',Nevada:'NV','New Hampshire':'NH','New Jersey':'NJ',
  'New Mexico':'NM','New York':'NY','North Carolina':'NC','North Dakota':'ND',
  Ohio:'OH',Oklahoma:'OK',Oregon:'OR',Pennsylvania:'PA','Rhode Island':'RI',
  'South Carolina':'SC','South Dakota':'SD',Tennessee:'TN',Texas:'TX',
  Utah:'UT',Vermont:'VT',Virginia:'VA',Washington:'WA','West Virginia':'WV',
  Wisconsin:'WI',Wyoming:'WY',
};

const IntelligenceMarkets = () => {
  const [marketData, setMarketData] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [labelsReady, setLabelsReady] = useState(false);
  const mapRef = useRef(null);

  useEffect(() => {
    Promise.all([
      fetchWithAuth(`${API_URL}/api/intelligence/markets`),
      fetchWithAuth(`${API_URL}/api/intelligence/industries`),
    ]).then(([markets]) => {
      setMarketData(markets);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Inject SVG text labels after map renders
  useEffect(() => {
    if (loading || labelsReady) return;
    const timer = setTimeout(() => {
      const container = mapRef.current;
      if (!container) return;
      const svg = container.querySelector('svg');
      if (!svg) return;
      const paths = svg.querySelectorAll('path');
      if (paths.length === 0) return;

      // Remove any existing labels
      svg.querySelectorAll('.state-lbl').forEach(el => el.remove());

      const NS = 'http://www.w3.org/2000/svg';

      // For each path (state), compute its bounding box center and add a label
      // The Geography paths don't have data attributes, so we match by index
      // Instead, match path centroids with known state positions
      // Simpler: just iterate paths and compute their visual center
      paths.forEach((path) => {
        try {
          const bbox = path.getBBox();
          if (bbox.width < 1 || bbox.height < 1) return;

          // Get the path's center
          const cx = bbox.x + bbox.width / 2;
          const cy = bbox.y + bbox.height / 2;

          // We don't know which state this path represents from SVG alone
          // So we'll skip individual labeling by path matching
        } catch (e) { /* getBBox may fail */ }
      });

      // Instead, build a lookup of state names from the Geographies
      // Since we can't access state names from paths, use the topology directly
      fetch(GEO_URL)
        .then(r => r.json())
        .then(topo => {
          // Dynamically import topojson-client
          import('topojson-client').then(({ feature }) => {
            const geo = feature(topo, topo.objects.states);
            // We need the projection to convert geo coords to SVG coords
            // ComposableMap uses geoAlbersUsa().scale(900).translate([400,300])
            import('d3-geo').then(({ geoAlbersUsa, geoPath: gp }) => {
              const projection = geoAlbersUsa().scale(900).translate([400, 300]);
              const pathGen = gp().projection(projection);

              geo.features.forEach(f => {
                const name = f.properties.name;
                const abbr = STATE_NAME_TO_ABBR[name];
                if (!abbr) return;
                const centroid = pathGen.centroid(f);
                if (!centroid || isNaN(centroid[0]) || isNaN(centroid[1])) return;

                // Background stroke for readability
                const bgText = document.createElementNS(NS, 'text');
                bgText.setAttribute('x', centroid[0]);
                bgText.setAttribute('y', centroid[1]);
                bgText.setAttribute('text-anchor', 'middle');
                bgText.setAttribute('dominant-baseline', 'central');
                bgText.setAttribute('font-size', '11');
                bgText.setAttribute('font-weight', '800');
                bgText.setAttribute('font-family', 'system-ui, -apple-system, sans-serif');
                bgText.setAttribute('fill', '#e2e8f0');
                bgText.setAttribute('stroke', '#e2e8f0');
                bgText.setAttribute('stroke-width', '4');
                bgText.setAttribute('stroke-linejoin', 'round');
                bgText.classList.add('state-lbl');
                bgText.textContent = abbr;
                bgText.style.pointerEvents = 'none';
                svg.appendChild(bgText);

                // Foreground text
                const text = document.createElementNS(NS, 'text');
                text.setAttribute('x', centroid[0]);
                text.setAttribute('y', centroid[1]);
                text.setAttribute('text-anchor', 'middle');
                text.setAttribute('dominant-baseline', 'central');
                text.setAttribute('font-size', '11');
                text.setAttribute('font-weight', '800');
                text.setAttribute('font-family', 'system-ui, -apple-system, sans-serif');
                text.setAttribute('fill', '#1e293b');
                text.classList.add('state-lbl');
                text.textContent = abbr;
                text.style.pointerEvents = 'none';
                svg.appendChild(text);
              });
              setLabelsReady(true);
            });
          });
        }).catch(() => {});
    }, 2000);
    return () => clearTimeout(timer);
  }, [loading, labelsReady]);

  const handleStateClick = async (stateName) => {
    try {
      const data = await fetchWithAuth(`${API_URL}/api/intelligence/snapshot?state=${encodeURIComponent(stateName)}`);
      setSnapshot(data);
    } catch { setSnapshot(null); }
  };

  const stateCountMap = {};
  if (marketData?.by_state) {
    marketData.by_state.forEach(s => { stateCountMap[s.state] = s.count; });
  }
  const counts = Object.values(stateCountMap);
  const colorScale = counts.length > 0
    ? scaleQuantile().domain(counts).range(COLOR_RANGE)
    : () => NO_DATA_COLOR;

  return (
    <IntelligenceLayout title="Markets">
      <header className="bg-white border-b border-gray-200 px-8 py-5 shadow-sm">
        <h1 className="text-2xl font-bold text-[#0A1F44]">Market Intelligence</h1>
        <p className="text-sm text-gray-500">Where is hiring happening? Click a state on the map for a market snapshot.</p>
      </header>

      <main className="p-8 space-y-6">
        {loading ? (
          <div className="text-center py-20 text-gray-500">Loading market data...</div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-[#e2e8f0] border border-gray-300 rounded-xl p-5 shadow-sm" data-testid="us-map" ref={mapRef}>
                <h3 className="text-sm font-semibold text-[#0A1F44] mb-3 uppercase tracking-wider">Job Demand by State</h3>
                <ComposableMap projection="geoAlbersUsa" projectionConfig={{ scale: 900 }}>
                  <Geographies geography={GEO_URL}>
                    {({ geographies }) =>
                      geographies.map(geo => {
                        const name = geo.properties.name;
                        const count = stateCountMap[name] || 0;
                        return (
                          <Geography key={geo.rsmKey} geography={geo}
                            onClick={() => handleStateClick(name)}
                            style={{
                              default: {
                                fill: count > 0 ? colorScale(count) : NO_DATA_COLOR,
                                stroke: '#94a3b8',
                                strokeWidth: 0.6,
                                outline: 'none',
                              },
                              hover: { fill: '#D4AF37', stroke: '#D4AF37', strokeWidth: 1.2, outline: 'none', cursor: 'pointer' },
                              pressed: { fill: '#b8972e', outline: 'none' },
                            }}
                          />
                        );
                      })
                    }
                  </Geographies>
                </ComposableMap>
                <div className="flex items-center gap-1.5 mt-2 justify-center">
                  <span className="text-[10px] text-gray-500">No data</span>
                  <div className="w-5 h-3 rounded-sm" style={{ backgroundColor: NO_DATA_COLOR }} />
                  <span className="text-[10px] text-gray-500 ml-2">Fewer</span>
                  {COLOR_RANGE.map((c, i) => (
                    <div key={i} className="w-5 h-3 rounded-sm" style={{ backgroundColor: c }} />
                  ))}
                  <span className="text-[10px] text-gray-500">More postings</span>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="market-snapshot">
                {snapshot ? (
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-[#0A1F44] flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-[#D4AF37]" /> {snapshot.label}
                    </h3>
                    <p className="text-xs text-gray-400">{snapshot.period}</p>
                    <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-[#D4AF37]">{snapshot.total_postings.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">Total Postings</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase mb-2">Top Roles</p>
                      {snapshot.top_roles.map((r, i) => (
                        <div key={i} className="flex justify-between text-sm py-1 border-b border-gray-100">
                          <span className="text-gray-700">{r.title}</span>
                          <span className="text-gray-500">{r.count}</span>
                        </div>
                      ))}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase mb-2">Top Industries</p>
                      {snapshot.top_industries.map((ind, i) => (
                        <div key={i} className="flex justify-between text-sm py-1 border-b border-gray-100">
                          <span className="text-gray-700">{ind.industry}</span>
                          <span className="text-gray-500">{ind.count}</span>
                        </div>
                      ))}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase mb-2">Top Companies</p>
                      {snapshot.top_companies.map((c, i) => (
                        <div key={i} className="flex justify-between text-sm py-1 border-b border-gray-100">
                          <span className="text-gray-700 truncate">{c.company}</span>
                          <span className="text-gray-500 flex-shrink-0 ml-2">{c.count}</span>
                        </div>
                      ))}
                    </div>
                    <div className="pt-2">
                      <p className="text-xs text-gray-500">Remote share: <span className="text-[#0A1F44] font-medium">{snapshot.remote_share}%</span></p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <MapPin className="h-8 w-8 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm">Click a state on the map</p>
                    <p className="text-gray-400 text-xs mt-1">to see its hiring snapshot</p>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="top-states-table">
                <h3 className="text-sm font-semibold text-[#0A1F44] mb-3 uppercase tracking-wider">Top States</h3>
                <div className="space-y-2">
                  {(marketData?.by_state || []).slice(0, 12).map((s, i) => {
                    const maxCount = marketData.by_state[0]?.count || 1;
                    return (
                      <div key={s.state} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 rounded-lg p-1.5 transition-colors" onClick={() => handleStateClick(s.state)}>
                        <span className="text-xs text-gray-400 w-5">{i + 1}</span>
                        <span className="text-sm text-gray-700 w-32 truncate">{s.state}</span>
                        <div className="flex-1 bg-gray-100 rounded-full h-2">
                          <div className="bg-[#D4AF37] h-2 rounded-full" style={{ width: `${(s.count / maxCount) * 100}%` }} />
                        </div>
                        <span className="text-sm text-gray-500 w-12 text-right">{s.count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="top-cities-table">
                <h3 className="text-sm font-semibold text-[#0A1F44] mb-3 uppercase tracking-wider">Top Cities / Metros</h3>
                <div className="space-y-2">
                  {(marketData?.by_city || []).slice(0, 12).map((c, i) => {
                    const maxCount = marketData.by_city[0]?.count || 1;
                    return (
                      <div key={`${c.city}-${c.state}`} className="flex items-center gap-3 p-1.5">
                        <span className="text-xs text-gray-400 w-5">{i + 1}</span>
                        <span className="text-sm text-gray-700 w-40 truncate">{c.city}{c.state ? `, ${c.state}` : ''}</span>
                        <div className="flex-1 bg-gray-100 rounded-full h-2">
                          <div className="bg-[#2563eb] h-2 rounded-full" style={{ width: `${(c.count / maxCount) * 100}%` }} />
                        </div>
                        <span className="text-sm text-gray-500 w-12 text-right">{c.count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </IntelligenceLayout>
  );
};

export default IntelligenceMarkets;
